/**
 * MatchAnalysis — Skills gap / match analysis section for JobDetailPage.
 * Calls the vacancy match endpoint and displays a breakdown of:
 * - Overall match score with progress bar
 * - Matched / missing skills chips
 * - Experience match details
 * - Strengths (bullet list)
 * - Recommendations (add / emphasize / reword)
 *
 * After a successful analysis the pipeline job's matchScore is updated
 * in-store so the header badge reflects the latest result.
 */

import { useState } from 'react';
import { Button } from '@/components/common';
import { vacancyService, type MatchDetails } from '@/services/vacancy.service';
import { usePipelineStore } from '@/store/pipeline.store';

interface MatchAnalysisProps {
  jobId: string;
  matchScore: number;
  hasDescription: boolean;
}

function getScoreColor(score: number): string {
  if (score >= 70) return 'var(--color-success)';
  if (score >= 50) return 'var(--color-warning)';
  return 'var(--color-error)';
}

function getScoreFillClass(score: number): string {
  if (score >= 70) return 'match-analysis__score-fill--high';
  if (score >= 50) return 'match-analysis__score-fill--medium';
  return 'match-analysis__score-fill--low';
}

const RECOMMENDATION_PREFIX: Record<MatchDetails['recommendations'][number]['type'], string> = {
  add: 'Add',
  emphasize: 'Emphasize',
  reword: 'Reword',
};

export function MatchAnalysis({ jobId, matchScore, hasDescription }: MatchAnalysisProps) {
  const [matchDetails, setMatchDetails] = useState<MatchDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateJob = usePipelineStore((s) => s.updateJob);

  const handleAnalyze = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await vacancyService.matchVacancy(jobId);
      setMatchDetails(result);
      updateJob(jobId, { matchScore: result.matchScore });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze match';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const hasResults = matchDetails !== null;
  const displayScore = hasResults ? matchDetails.matchScore : matchScore;
  const buttonLabel = matchScore > 0 ? 'Re-analyze' : 'Analyze Profile vs. Job';

  return (
    <div className="match-analysis">
      {/* Header row */}
      <div className="match-analysis__header">
        <h3 className="match-analysis__title">Match Analysis</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={handleAnalyze}
          isLoading={isLoading}
          disabled={!hasDescription || isLoading}
        >
          {buttonLabel}
        </Button>
      </div>

      {/* No description state */}
      {!hasDescription && (
        <p className="match-analysis__empty">
          Add a job description to enable match analysis.
        </p>
      )}

      {/* Loading message */}
      {isLoading && (
        <p className="match-analysis__loading-msg">Analyzing your profile against this job...</p>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <p className="match-analysis__error">{error}</p>
      )}

      {/* Results */}
      {hasResults && !isLoading && (
        <div className="match-analysis__results">
          {/* Overall score */}
          <div className="match-analysis__score-section">
            <div className="match-analysis__score-row">
              <span
                className="match-analysis__score-value"
                style={{ color: getScoreColor(displayScore) }}
              >
                {displayScore}%
              </span>
              <span className="match-analysis__score-label">Overall Match</span>
            </div>
            <div className="match-analysis__score-bar">
              <div
                className={`match-analysis__score-fill ${getScoreFillClass(displayScore)}`}
                style={{ width: `${displayScore}%` }}
              />
            </div>
          </div>

          {/* Skills section */}
          <div className="match-analysis__section">
            <p className="match-analysis__section-title">
              Skills Match ({matchDetails.breakdown.skills.percentage}%)
            </p>
            {matchDetails.breakdown.skills.matched.length > 0 && (
              <div className="match-analysis__chips">
                {matchDetails.breakdown.skills.matched.map((skill, i) => (
                  <span key={i} className="match-analysis__skill match-analysis__skill--matched">
                    ✓ {skill}
                  </span>
                ))}
              </div>
            )}
            {matchDetails.breakdown.skills.missing.length > 0 && (
              <div className="match-analysis__chips">
                {matchDetails.breakdown.skills.missing.map((skill, i) => (
                  <span key={i} className="match-analysis__skill match-analysis__skill--missing">
                    ✗ {skill}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Experience section */}
          <div className="match-analysis__section">
            <p className="match-analysis__section-title">
              Experience ({matchDetails.breakdown.experience.score}%)
            </p>
            {matchDetails.breakdown.experience.matched.length > 0 && (
              <ul className="match-analysis__list">
                {matchDetails.breakdown.experience.matched.map((item, i) => (
                  <li key={i} className="match-analysis__list-item match-analysis__list-item--matched">
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {matchDetails.breakdown.experience.missing.length > 0 && (
              <ul className="match-analysis__list">
                {matchDetails.breakdown.experience.missing.map((item, i) => (
                  <li key={i} className="match-analysis__list-item match-analysis__list-item--missing">
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {matchDetails.breakdown.experience.years > 0 && (
              <p className="match-analysis__experience-years">
                Required: {matchDetails.breakdown.experience.years} year
                {matchDetails.breakdown.experience.years !== 1 ? 's' : ''} of experience
              </p>
            )}
          </div>

          {/* Strengths */}
          {matchDetails.strengths.length > 0 && (
            <div className="match-analysis__section">
              <p className="match-analysis__section-title">Strengths</p>
              <ul className="match-analysis__list">
                {matchDetails.strengths.map((strength, i) => (
                  <li key={i} className="match-analysis__list-item match-analysis__list-item--strength">
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {matchDetails.recommendations.length > 0 && (
            <div className="match-analysis__section">
              <p className="match-analysis__section-title">Recommendations</p>
              <ul className="match-analysis__recommendations">
                {matchDetails.recommendations.map((rec, i) => (
                  <li key={i} className="match-analysis__recommendation">
                    <span className={`match-analysis__rec-type match-analysis__rec-type--${rec.type}`}>
                      {RECOMMENDATION_PREFIX[rec.type]}
                    </span>
                    <span className="match-analysis__rec-suggestion">{rec.suggestion}</span>
                    <span className="match-analysis__rec-reason">{rec.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
